/**
 * BulkSendPage
 *
 * One bulk send, live: each person's copy is made and sent in the background,
 * and this page follows it — "Sending… 37 of 500", then "14 of 20 signed" as
 * people sign. It refreshes on its own while copies are being made or are out
 * for signature (`useBulkBatch`).
 *
 * Reached right after a bulk send starts, and from the "Bulk send" tag on any
 * copy in the envelope list (the owner's own copies only — the overview is
 * owner-scoped).
 */

import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { HolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useBulkBatch } from '../hooks';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import type { DocumentSignBulkSendItem } from '@/lib/api';

/** What a person's row says. Mirrors the copy's lifecycle, in plain words. */
export function bulkItemStatusLabel(item: DocumentSignBulkSendItem): string {
  if (item.status === 'pending' || item.status === 'processing') return 'Preparing';
  if (item.status === 'failed') return 'Not sent';
  switch (item.envelopeStatus) {
    case 'completed':
      return 'Signed';
    case 'declined':
      // Asking for a paper copy is not refusing the agreement — the audit
      // trail keeps them apart, and so does this.
      return item.recipientStatus === 'declined_electronic' ? 'Asked for paper' : 'Declined';
    case 'voided':
      return 'Voided';
    case 'expired':
      return 'Expired';
    default:
      return item.recipientStatus === 'viewed' ? 'Opened' : 'Waiting';
  }
}

export function BulkSendPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { batch, loading, error } = useBulkBatch(batchId);
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  const header = (
    <ArcheHeader archeId="arche_document_sign" title="Document Sign">
      <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
      <Button variant="outline" size="sm" onClick={() => navigate('/a/arche_document_sign')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to envelopes
      </Button>
    </ArcheHeader>
  );

  // Only a FIRST load failure replaces the page. A failed refresh keeps the
  // list on screen (it is still the last known state) with a note above it.
  if (error && !batch) {
    return (
      <div className="flex flex-col h-full">
        {header}
        <div className="max-w-3xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (loading || !batch) {
    return (
      <div className="flex flex-col h-full">
        {header}
        <div className="max-w-3xl mx-auto p-6 w-full space-y-3" aria-busy="true">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const { items } = batch;
  const processing = batch.status === 'processing';
  const sent = items.filter((i) => i.status === 'sent').length;
  const signed = items.filter((i) => i.envelopeStatus === 'completed').length;
  const failed = items.filter((i) => i.status === 'failed');

  const Icon = processing ? Loader2 : failed.length > 0 ? AlertTriangle : CheckCircle2;
  const iconClass = processing
    ? 'h-8 w-8 text-primary shrink-0 animate-spin motion-reduce:animate-none'
    : failed.length > 0
      ? 'h-8 w-8 text-amber-600 dark:text-amber-400 shrink-0'
      : 'h-8 w-8 text-primary shrink-0';

  return (
    <div className="flex flex-col h-full">
      {header}
      <div className="max-w-3xl mx-auto p-6 w-full space-y-6">
        <div className="flex items-start gap-3">
          <Icon className={iconClass} aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold">{batch.title}</h1>
            <p className="text-sm text-muted-foreground" data-testid="bulk-send-progress">
              {processing
                ? `Bulk send · sending… ${sent + failed.length} of ${batch.total} done`
                : `Bulk send · ${signed} of ${batch.total} signed`}
            </p>
            {/* Announced once when the send finishes — not on every refresh,
                which would read out a count every few seconds. */}
            <p className="sr-only" role="status">
              {processing
                ? ''
                : `Bulk send finished: ${sent} sent${failed.length > 0 ? `, ${failed.length} not sent` : ''}.`}
            </p>
          </div>
        </div>

        {error && (
          <p className="text-sm text-muted-foreground" data-testid="bulk-send-refresh-error">
            Couldn’t refresh just now — showing the last update. It will try again.
          </p>
        )}

        {failed.length > 0 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
            {failed.length} {failed.length === 1 ? 'copy was' : 'copies were'} not sent. Each one
            below says why, and whether it was charged.
          </div>
        )}

        <ul className="divide-y rounded-md border" aria-label="People">
          {items.map((item) => {
            const canOpen = item.envelopeId !== null;
            const isDraft = item.envelopeStatus === 'draft';
            return (
              <li key={item.itemId} className="flex items-start justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{item.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{item.email}</div>
                  {item.problem && (
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                      {item.problem}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {bulkItemStatusLabel(item)}
                  </span>
                  {canOpen && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        navigate(
                          isDraft
                            ? `/a/arche_document_sign/envelope/${item.envelopeId}`
                            : `/a/arche_document_sign/envelope/${item.envelopeId}/detail`,
                        )
                      }
                      aria-label={`${isDraft ? 'Open' : 'View'} ${item.name}'s copy`}
                    >
                      {isDraft ? 'Open' : 'View'}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
